import React from 'react'
import {Controller, UseFormMethods} from 'react-hook-form'
import Alert from '@material-ui/lab/Alert'
import AlertTitle from '@material-ui/lab/AlertTitle'
import Typography from '@material-ui/core/Typography'
import HTMLEditor, {HTMLEditorContainer} from 'lib/ui/form/HTMLEditor'
import {Label} from 'lib/ui/typography'
import TextField from 'lib/ui/TextField'
import TextArea from 'lib/ui/TextField/TextArea'
import {useAttendeeVariables} from 'Event'
import {Snackbar} from 'lib/ui/Snackbar'
import ColorPicker from 'lib/ui/ColorPicker'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import styled from 'styled-components'
import {useUserTranslations} from 'Event/LanguageProvider/translations'
import {useTownhallTemplate} from 'Event/template/Townhall'

export default function Settings(props: {
  control: UseFormMethods['control']
  register: UseFormMethods['register']
  watch: UseFormMethods['watch']
}) {
  const template = useTownhallTemplate()
  const {leaderboard, rewardAlert} = template
  const {control, register, watch} = props

  const v = useAttendeeVariables()
  const translate = useUserTranslations()

  const rewardText: string =
    watch('rewardAlert.text') ?? translate(rewardAlert.text)
  const rewardTextColor: string =
    watch('rewardAlert.textColor') ?? rewardAlert.textColor
  const rewardBackgroundColor: string =
    watch('rewardAlert.backgroundColor') ?? rewardAlert.backgroundColor

  return (
    <>
      <Alert severity="info">
        <AlertTitle>Variables</AlertTitle>
        <div>
          <Typography variant="caption">
            {`{{points_unit}} - Name for points`}
          </Typography>
        </div>
        <div>
          <Typography variant="caption">
            {`{{action_description}} - Action description`}
          </Typography>
        </div>
        <div>
          <Typography variant="caption">
            {`{{action_points}} - Number of points received`}
          </Typography>
        </div>
        <div>
          <Typography variant="caption">
            {`{{leaderboard_points}} - Attendee's current points`}
          </Typography>
        </div>
        <div>
          <Typography variant="caption">
            {`{{leaderboard_position}} - Attendee's current position on leaderboard`}
          </Typography>
        </div>
      </Alert>
      <Label mt={6}>Title</Label>
      <TextField
        name="title"
        aria-label="set leaderboard page title"
        defaultValue={v(leaderboard.title)}
        fullWidth
        inputProps={{
          ref: register({
            required: 'Title is required',
          }),
        }}
      />
      <Controller
        name="description"
        control={control}
        defaultValue={v(leaderboard.description)}
        rules={{
          required: 'Description is required',
        }}
        render={({value, onChange}) => (
          <HTMLEditorContainer>
            <HTMLEditor
              data={value}
              onChange={onChange}
              customLinks={['Leaderboard']}
            />
          </HTMLEditorContainer>
        )}
      />
      <Label>Back to Dashboard Text</Label>
      <TextField
        name="backToDashboardText"
        aria-label="set leaderboard page back to dashboard text"
        defaultValue={v(leaderboard.backToDashboardText)}
        fullWidth
        inputProps={{
          ref: register({
            required: 'Back to dashboard text is required',
          }),
        }}
      />

      <Controller
        name="backToDashboardTextColor"
        control={control}
        defaultValue={leaderboard.backToDashboardTextColor}
        render={({value, onChange}) => (
          <ColorPicker
            label="Back to Dashboard Text Color"
            color={value}
            onPick={onChange}
            aria-label="set back to dashboard text color"
          />
        )}
      />

      <Label>Points Unit</Label>
      <TextField
        name="points_unit"
        aria-label="points unit"
        fullWidth
        defaultValue={v(template.points_unit)}
        inputProps={{
          ref: register({
            required: 'Points unit is required',
          }),
        }}
      />

      <Snackbar
        style={{marginBottom: '24px'}}
        message={
          <Container>
            <CheckCircleIcon />
            <StyledDiv>{rewardText}</StyledDiv>
          </Container>
        }
        color={rewardTextColor}
        backgroundColor={rewardBackgroundColor}
      />
      <ColorPickerContainer>
        <Controller
          name="rewardAlert.backgroundColor"
          control={control}
          defaultValue={rewardAlert.backgroundColor}
          render={({value, onChange}) => (
            <ColorPicker
              label="Popup Background Color"
              color={value}
              onPick={onChange}
            />
          )}
        />
        <Controller
          name="rewardAlert.textColor"
          control={control}
          defaultValue={rewardAlert.textColor}
          render={({value, onChange}) => (
            <ColorPicker label="Popup Color" color={value} onPick={onChange} />
          )}
        />
      </ColorPickerContainer>

      <Label>Popup Text</Label>
      <TextArea
        name="rewardAlert.text"
        label="Popup Text"
        aria-label="set leaderboard popup text"
        fullWidth
        defaultValue={v(template.rewardAlert.text)}
        rows={3}
        variant="outlined"
        inputProps={{
          ref: register({required: 'Title is required'}),
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
            }
          },
        }}
      />
    </>
  )
}

const Container = styled.div`
  display: flex;
`
const StyledDiv = styled.div`
  font-size: 19px;
`
const ColorPickerContainer = styled.div`
  display: flex;
`
